import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { crvPhaseService } from '../../services/phaseService';
import { CrvPhase } from '../../types/Phase';
import '../users/UserForm.css';

interface CrvPhaseFormProps {
  productId: string;
  phase: CrvPhase | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const CrvPhaseForm: React.FC<CrvPhaseFormProps> = ({ productId, phase, onSuccess, onCancel }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<CrvPhase>({
    nombreFase: '',
    capObjetivoFase: undefined,
    capObjetivoActual: undefined,
    fechaInicioFase: '',
    fechaInicioActual: '',
    fechaDevolucionEstInicial: '',
    fechaDevolucionEstActual: '',
    interesTotal: undefined,
    interesAnualCuenta: undefined,
    intFidelizacion: 1.5,
    retencionIrpf: 19,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (phase) {
      setFormData(phase);
    }
  }, [phase]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const numericFields = ['capObjetivoFase', 'capObjetivoActual', 'interesTotal', 'interesAnualCuenta', 'intFidelizacion', 'retencionIrpf'];

    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: numericFields.includes(name) ? (value ? parseFloat(value) : undefined) : value,
      };

      // Si se cambia fechaDevolucionEstInicial y fechaDevolucionEstActual está vacía, copiar el valor
      if (name === 'fechaDevolucionEstInicial' && !prev.fechaDevolucionEstActual) {
        newData.fechaDevolucionEstActual = value;
      }

      return newData;
    });
  };

  const calculateDesviacionDias = (): number | null => {
    if (!formData.fechaDevolucionEstInicial || !formData.fechaDevolucionEstActual) {
      return null;
    }
    const fechaInicial = new Date(formData.fechaDevolucionEstInicial);
    const fechaActual = new Date(formData.fechaDevolucionEstActual);
    const diffTime = fechaActual.getTime() - fechaInicial.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateRtoAnualInicial = (): number | null => {
    if (!formData.fechaInicioFase || !formData.fechaDevolucionEstInicial || !formData.interesTotal) {
      return null;
    }
    const fechaInicio = new Date(formData.fechaInicioFase);
    const fechaDevolucion = new Date(formData.fechaDevolucionEstInicial);
    const diffTime = fechaDevolucion.getTime() - fechaInicio.getTime();
    const dias = Math.round(diffTime / (1000 * 60 * 60 * 24));
    if (dias <= 1) return null;
    return (formData.interesTotal / (dias - 1)) * 365;
  };

  const calculateRtoAnualActual = (): number | null => {
    if (!formData.fechaInicioFase || !formData.fechaDevolucionEstActual || !formData.interesTotal) {
      return null;
    }
    const fechaInicio = new Date(formData.fechaInicioFase);
    const fechaDevolucion = new Date(formData.fechaDevolucionEstActual);
    const diffTime = fechaDevolucion.getTime() - fechaInicio.getTime();
    const dias = Math.round(diffTime / (1000 * 60 * 60 * 24));
    if (dias <= 1) return null;
    return (formData.interesTotal / (dias - 1)) * 365;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (phase?.id) {
        await crvPhaseService.update(productId, phase.id, formData);
        showToast('Fase CRV actualizada correctamente');
      } else {
        await crvPhaseService.create(productId, formData);
        showToast('Fase CRV creada correctamente');
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar la fase CRV');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-form phase-form">
      <h3>{phase ? 'Editar Fase CRV' : 'Nueva Fase CRV'}</h3>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <h4>Identificación</h4>
        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="nombreFase">Nombre de la Fase *</label>
            <input
              type="text"
              id="nombreFase"
              name="nombreFase"
              value={formData.nombreFase}
              onChange={handleChange}
              required
              maxLength={200}
            />
          </div>
          <div className="form-group">
            <label htmlFor="capObjetivoFase">Capital Objetivo</label>
            <input
              type="number"
              id="capObjetivoFase"
              name="capObjetivoFase"
              value={formData.capObjetivoFase || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="capObjetivoActual">Capital Objetivo Actual</label>
            <input
              type="number"
              id="capObjetivoActual"
              name="capObjetivoActual"
              value={formData.capObjetivoActual || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
          </div>
        </div>

        <h4>Fechas</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fechaInicioFase">Fecha Inicio</label>
            <input
              type="date"
              id="fechaInicioFase"
              name="fechaInicioFase"
              value={formData.fechaInicioFase || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaInicioActual">Fecha Inicio Actual</label>
            <input
              type="date"
              id="fechaInicioActual"
              name="fechaInicioActual"
              value={formData.fechaInicioActual || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaDevolucionEstInicial">Devolucion Est. Inicial</label>
            <input
              type="date"
              id="fechaDevolucionEstInicial"
              name="fechaDevolucionEstInicial"
              value={formData.fechaDevolucionEstInicial || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaDevolucionEstActual">Devolucion Est. Actual</label>
            <input
              type="date"
              id="fechaDevolucionEstActual"
              name="fechaDevolucionEstActual"
              value={formData.fechaDevolucionEstActual || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Desviación Días</label>
            <input
              type="text"
              value={calculateDesviacionDias()?.toString() ?? '-'}
              disabled
            />
          </div>
        </div>

        <h4>Condiciones</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="interesTotal">Interés Total (%)</label>
            <input
              type="number"
              id="interesTotal"
              name="interesTotal"
              value={formData.interesTotal || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
              max="100"
            />
          </div>
          <div className="form-group">
            <label htmlFor="interesAnualCuenta">Interés Anual Cuenta (%)</label>
            <input
              type="number"
              id="interesAnualCuenta"
              name="interesAnualCuenta"
              value={formData.interesAnualCuenta || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
              max="100"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="intFidelizacion">Int. Fidelizacion (%)</label>
            <input
              type="number"
              id="intFidelizacion"
              name="intFidelizacion"
              value={formData.intFidelizacion || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
              max="100"
            />
          </div>
          <div className="form-group">
            <label htmlFor="retencionIrpf">Retencion IRPF (%)</label>
            <input
              type="number"
              id="retencionIrpf"
              name="retencionIrpf"
              value={formData.retencionIrpf || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
              max="100"
            />
          </div>
        </div>

        <h4>Rendimientos</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Rto. Anual Inicial (%)</label>
            <input
              type="text"
              value={calculateRtoAnualInicial()?.toFixed(2) ?? '-'}
              disabled
            />
          </div>
          <div className="form-group">
            <label>Rto. Anual Actual (%)</label>
            <input
              type="text"
              value={calculateRtoAnualActual()?.toFixed(2) ?? '-'}
              disabled
            />
          </div>
        </div>

        {phase && (
          <>
            <h4>Control Admin</h4>
            <div className="form-row four-columns">
              <div className="form-group">
                <label>Pte. Envío Contrato (Nº)</label>
                <input type="text" value={formData.pteEnvioContratoNum ?? 0} disabled />
              </div>
              <div className="form-group">
                <label>Pte. Envío Contrato (€)</label>
                <input type="text" value={formatCurrency(formData.pteEnvioContratoEur)} disabled />
              </div>
              <div className="form-group">
                <label>Pte. Firma (Nº)</label>
                <input type="text" value={formData.pteFirmaNum ?? 0} disabled />
              </div>
              <div className="form-group">
                <label>Pte. Firma (€)</label>
                <input type="text" value={formatCurrency(formData.pteFirmaEur)} disabled />
              </div>
            </div>
            <div className="form-row four-columns">
              <div className="form-group">
                <label>Pte. Transferencia (Nº)</label>
                <input type="text" value={formData.pteTransferenciaNum ?? 0} disabled />
              </div>
              <div className="form-group">
                <label>Pte. Transferencia (€)</label>
                <input type="text" value={formatCurrency(formData.pteTransferenciaEur)} disabled />
              </div>
              <div className="form-group">
                <label>Rechazado (Nº)</label>
                <input type="text" value={formData.rechazadoNum ?? 0} disabled />
              </div>
              <div className="form-group">
                <label>Rechazado (€)</label>
                <input type="text" value={formatCurrency(formData.rechazadoEur)} disabled />
              </div>
            </div>
          </>
        )}

        {phase && (
          <>
            <h4>Métricas Capital</h4>
            <div className="form-row four-columns">
              <div className="form-group">
                <label>Num Inversores</label>
                <input type="text" value={formData.numInversores || 0} disabled />
              </div>
              <div className="form-group">
                <label>Total Captado</label>
                <input type="text" value={formatCurrency(formData.totalCaptado)} disabled />
              </div>
              <div className="form-group">
                <label>Total Devuelto</label>
                <input type="text" value={formatCurrency(formData.totalDevuelto)} disabled />
              </div>
              <div className="form-group">
                <label>Capital Pte. Captar</label>
                <input type="text" value={formatCurrency(formData.capPdteCaptar)} disabled />
              </div>
            </div>

            <h4>Métricas Interés</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Int. Fidelización (€)</label>
                <input type="text" value={formatCurrency(formData.intFidelizacionEur)} disabled />
              </div>
              <div className="form-group">
                <label>Intereses Devengados</label>
                <input type="text" value={formatCurrency(formData.interesesDevengados)} disabled />
              </div>
              <div className="form-group">
                <label>Total Int. Devengados</label>
                <input type="text" value={formatCurrency(formData.totalIntDevengados)} disabled />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Intereses Anticipados</label>
                <input type="text" value={formatCurrency(formData.interesesAnticipados)} disabled />
              </div>
              <div className="form-group">
                <label>Liq. Final Pendiente</label>
                <input type="text" value={formatCurrency(formData.liqFinalPendiente)} disabled />
              </div>
            </div>
          </>
        )}

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : phase ? 'Actualizar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
};

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
};

export default CrvPhaseForm;
